import { useMutation, useQueryClient } from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { queryKeysCompany } from '../queryKeys';

import {
  type UpdateCompanyAliasAddressMutationOptions,
  type UpdateCompanyAliasAddressPayload,
} from './types';
import { updateCompanyAliasAddress } from './updateCompanyAliasAddress';

/**
 * Custom hook for updating a company alias address using mutations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company-Alias-Addresses/operation/update_company_address_vadmin_company_alias_address__company_address_uuid__put
 */
export const useMutationUpdateCompanyAliasAddress = (
  options?: UpdateCompanyAliasAddressMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      aliasAddressId,
      companyAliasAddress,
    }: {
      aliasAddressId: UUID;
      companyAliasAddress: UpdateCompanyAliasAddressPayload;
    }) =>
      updateCompanyAliasAddress({
        aliasAddressId,
        companyAliasAddress,
      }),
    onSettled(_data, _error, { aliasAddressId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getAliasAddress(aliasAddressId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getAliasAddresses({}),
      });
    },
    ...options,
  });
};
